import { getDbSupabase } from './supabase';
import { formatSupabaseError } from './formatSupabaseError';
import { formatDisplayPersonName } from './formatPersonName';
import type {
  ApplicationSubmission,
  CertificateRequestAttachmentRef,
  CertificateRequestFormData,
  CertificateRequestItemRow,
  CertificateRequestTypeKey,
} from '../types/applicationForm';
import {
  EMPTY_CERTIFICATE_REQUEST_FORM,
  EMPTY_CERTIFICATE_REQUEST_ITEM,
  EMPTY_CERTIFICATE_REQUEST_TYPES,
} from '../types/applicationForm';

const TABLE = 'certificate_request_submissions';
const FORM_SLUG = 'certificate-request' as const;
const BUCKET = 'certificate-request-attachments';
const ITEM_ROWS = 12;

export type CertificateRequestAttachmentKind = 'customer_requirement' | 'sales_report';

const TYPE_KEYS: CertificateRequestTypeKey[] = [
  'food_grade_sgs',
  'msds',
  'sfa',
  'iso_22000',
  'others',
];

function withCtrSlug(row: ApplicationSubmission): ApplicationSubmission {
  return {
    ...row,
    form_slug: FORM_SLUG,
    form_data: normalizeCertificateRequestFormData(row.form_data),
  };
}

function mapRpcRow(data: unknown): ApplicationSubmission {
  return withCtrSlug(data as ApplicationSubmission);
}

function normalizeAttachment(raw: unknown): CertificateRequestAttachmentRef | null {
  if (!raw || typeof raw !== 'object') return null;
  const data = raw as Partial<CertificateRequestAttachmentRef>;
  const path = String(data.path ?? '').trim();
  if (!path) return null;
  const fileName = String(data.fileName ?? '').trim() || path.split('/').pop() || 'attachment';
  return {
    path,
    fileName,
    size: typeof data.size === 'number' ? data.size : undefined,
    mime: typeof data.mime === 'string' ? data.mime : undefined,
  };
}

export function normalizeCertificateRequestFormData(raw: unknown): CertificateRequestFormData {
  const data = (raw && typeof raw === 'object' ? raw : {}) as Partial<CertificateRequestFormData>;
  const rawItems = Array.isArray(data.items) ? data.items : [];
  const items: CertificateRequestItemRow[] = Array.from({ length: ITEM_ROWS }, (_, i) => {
    const row = rawItems[i];
    if (!row || typeof row !== 'object') return { ...EMPTY_CERTIFICATE_REQUEST_ITEM };
    return {
      item_no: String(row.item_no ?? ''),
      vendor_no: String(row.vendor_no ?? ''),
      material: String(row.material ?? ''),
      remarks: String(row.remarks ?? ''),
    };
  });

  const rawTypes =
    data.types && typeof data.types === 'object'
      ? (data.types as Partial<Record<CertificateRequestTypeKey, boolean>>)
      : {};
  const types = { ...EMPTY_CERTIFICATE_REQUEST_TYPES };
  for (const key of TYPE_KEYS) {
    types[key] = Boolean(rawTypes[key]);
  }

  const customerReqs = Array.isArray(data.customer_requirement_attachments)
    ? data.customer_requirement_attachments.map(normalizeAttachment).filter(Boolean)
    : [];
  const salesReports = Array.isArray(data.sales_report_attachments)
    ? data.sales_report_attachments.map(normalizeAttachment).filter(Boolean)
    : [];

  return {
    ...EMPTY_CERTIFICATE_REQUEST_FORM,
    ...data,
    types,
    type_others: String(data.type_others ?? ''),
    items,
    customer_requirement_attachments: customerReqs as CertificateRequestAttachmentRef[],
    sales_report_attachments: salesReports as CertificateRequestAttachmentRef[],
    posted_at: typeof data.posted_at === 'string' ? data.posted_at : undefined,
    posted_by: typeof data.posted_by === 'string' ? data.posted_by : undefined,
  };
}

export function companyNameFromCertificateRequest(form: CertificateRequestFormData): string {
  return form.customer_name.trim() || form.customer_no.trim() || 'Certificate Request';
}

function personFromRow(raw: string | null | undefined): string {
  return formatDisplayPersonName(raw) || String(raw ?? '').trim();
}

/**
 * Map workflow actors onto PU-040 signature fields:
 * Requested by = submitter, Prepared by = authorised (Updated by), Verified by = approver.
 */
export function enrichCertificateRequestSignatures(
  form: CertificateRequestFormData,
  row: Pick<ApplicationSubmission, 'submitted_by' | 'authorised_by' | 'approved_by'>,
  overrides?: Partial<{
    requested_by: string;
    prepared_by: string;
    verified_by: string;
  }>,
): CertificateRequestFormData {
  const requested =
    overrides?.requested_by?.trim() ||
    form.requested_by.trim() ||
    personFromRow(row.submitted_by);
  const prepared =
    overrides?.prepared_by?.trim() ||
    form.prepared_by.trim() ||
    personFromRow(row.authorised_by);
  const verified =
    overrides?.verified_by?.trim() ||
    form.verified_by.trim() ||
    personFromRow(row.approved_by);
  return {
    ...form,
    requested_by: requested,
    prepared_by: prepared,
    verified_by: verified,
  };
}

export function validateCertificateRequestForm(
  form: CertificateRequestFormData,
  opts?: {
    pendingCustomerRequirementFiles?: File[];
    /** Default false (temporarily optional). Set true to require at least one file. */
    requireCustomerAttachments?: boolean;
  },
): string | null {
  if (!form.customer_name.trim() && !form.customer_no.trim()) {
    return 'Enter customer name or customer number.';
  }
  const anyType = Object.values(form.types).some(Boolean);
  if (!anyType) return 'Select at least one certificate type.';
  if (form.types.others && !form.type_others.trim()) {
    return 'Specify the other certificate type.';
  }
  if (opts?.requireCustomerAttachments) {
    const pending = opts?.pendingCustomerRequirementFiles?.length ?? 0;
    const saved = form.customer_requirement_attachments.length;
    if (pending + saved === 0) {
      return 'Attach at least one customer requirement file (screenshot, email, or any supporting document).';
    }
  }
  return null;
}

export async function uploadCertificateRequestAttachment(
  submissionId: string,
  kind: CertificateRequestAttachmentKind,
  file: File,
): Promise<CertificateRequestAttachmentRef> {
  const db = getDbSupabase('forms');
  const safeName = file.name.replace(/[^\w.\-()+ ]+/g, '_').slice(0, 120) || kind;
  const path = `${submissionId}/${kind}/${Date.now()}_${safeName}`;
  const { error } = await db.storage.from(BUCKET).upload(path, file, {
    upsert: false,
    contentType: file.type || undefined,
  });
  if (error) throw new Error(error.message || 'Failed to upload attachment.');
  return {
    path,
    fileName: file.name,
    size: file.size,
    mime: file.type || undefined,
  };
}

export async function getCertificateRequestAttachmentSignedUrl(path: string): Promise<string> {
  const db = getDbSupabase('forms');
  const { data, error } = await db.storage.from(BUCKET).createSignedUrl(path, 60 * 30);
  if (error) throw new Error(error.message || 'Failed to open attachment.');
  if (!data?.signedUrl) throw new Error('Failed to open attachment.');
  return data.signedUrl;
}

export async function patchCertificateRequestAttachments(
  id: string,
  formData: CertificateRequestFormData,
): Promise<ApplicationSubmission> {
  const db = getDbSupabase('forms');
  const payload = normalizeCertificateRequestFormData(formData);
  const { data, error } = await db.rpc('patch_certificate_request_attachments', {
    p_id: id,
    p_form_data: payload,
  });
  if (error) throw new Error(formatSupabaseError(error));
  return mapRpcRow(data);
}

export async function fetchCertificateRequestSubmissions(): Promise<ApplicationSubmission[]> {
  const db = getDbSupabase('forms');
  const { data, error } = await db
    .from(TABLE)
    .select('*')
    .order('submitted_at', { ascending: false });
  if (error) throw new Error(formatSupabaseError(error));
  return (data ?? []).map((row) => withCtrSlug(row as ApplicationSubmission));
}

export async function fetchCertificateRequestSubmission(id: string): Promise<ApplicationSubmission> {
  const db = getDbSupabase('forms');
  const { data, error } = await db.from(TABLE).select('*').eq('id', id).maybeSingle();
  if (error) throw new Error(formatSupabaseError(error));
  if (!data) throw new Error('Submission not found.');
  return withCtrSlug(data as ApplicationSubmission);
}

export async function submitCertificateRequest(
  formData: CertificateRequestFormData,
  submittedBy?: string,
  submitterEmail?: string,
): Promise<ApplicationSubmission> {
  const db = getDbSupabase('forms');
  const payload = normalizeCertificateRequestFormData(formData);
  const { data, error } = await db.rpc('submit_certificate_request', {
    p_company_name: companyNameFromCertificateRequest(payload),
    p_form_data: payload,
    p_submitted_by: submittedBy?.trim() || null,
    p_submitter_email: submitterEmail?.trim() || null,
  });
  if (error) throw new Error(formatSupabaseError(error));
  return mapRpcRow(data);
}

export async function startCertificateRequestProcessing(
  id: string,
): Promise<ApplicationSubmission> {
  const db = getDbSupabase('forms');
  const { data, error } = await db.rpc('certificate_request_start_processing', {
    p_id: id,
  });
  if (error) throw new Error(formatSupabaseError(error));
  return mapRpcRow(data);
}

export async function updateCertificateRequestAuthorised(
  id: string,
  formData: CertificateRequestFormData,
): Promise<ApplicationSubmission> {
  const db = getDbSupabase('forms');
  const payload = normalizeCertificateRequestFormData(formData);
  const { data, error } = await db.rpc('update_certificate_request_authorised', {
    p_id: id,
    p_form_data: payload,
  });
  if (error) throw new Error(formatSupabaseError(error));
  return mapRpcRow(data);
}

/** @deprecated Prefer updateCertificateRequestAuthorised — kept for older call sites. */
export async function updateCertificateRequest(
  id: string,
  formData: CertificateRequestFormData,
): Promise<ApplicationSubmission> {
  return updateCertificateRequestAuthorised(id, formData);
}

export async function submitCertificateRequestForApproval(
  id: string,
  formData?: CertificateRequestFormData,
): Promise<ApplicationSubmission> {
  const db = getDbSupabase('forms');
  const { data, error } = await db.rpc('submit_certificate_request_for_approval', {
    p_id: id,
    p_form_data: formData ? normalizeCertificateRequestFormData(formData) : null,
  });
  if (error) throw new Error(formatSupabaseError(error));
  return mapRpcRow(data);
}

export async function rejectCertificateRequest(
  id: string,
  reason?: string | null,
  formData?: CertificateRequestFormData,
): Promise<ApplicationSubmission> {
  const db = getDbSupabase('forms');
  const { data, error } = await db.rpc('reject_certificate_request', {
    p_id: id,
    p_reason: reason?.trim() || null,
    p_form_data: formData ? normalizeCertificateRequestFormData(formData) : null,
  });
  if (error) throw new Error(formatSupabaseError(error));
  return mapRpcRow(data);
}

export async function approveCertificateRequest(
  id: string,
  formData?: CertificateRequestFormData,
): Promise<ApplicationSubmission> {
  const db = getDbSupabase('forms');
  const { data, error } = await db.rpc('approve_certificate_request', {
    p_id: id,
    p_form_data: formData ? normalizeCertificateRequestFormData(formData) : null,
  });
  if (error) throw new Error(formatSupabaseError(error));
  return mapRpcRow(data);
}

export function isCtrApproved(status: string | undefined | null): boolean {
  return status === 'approved' || status === 'posted';
}

export function isCtrCancelled(status: string | undefined | null): boolean {
  return status === 'cancelled';
}

/** @deprecated Use isCtrApproved — Post flow retired. */
export function isCtrPosted(status: string | undefined | null): boolean {
  return isCtrApproved(status);
}

export function certificateTypeLabels(form: CertificateRequestFormData): string {
  const labels: string[] = [];
  if (form.types.food_grade_sgs) labels.push('Food Grade (SGS)');
  if (form.types.msds) labels.push('MSDS');
  if (form.types.sfa) labels.push('SFA');
  if (form.types.iso_22000) labels.push('ISO 22000');
  if (form.types.others) {
    const other = form.type_others.trim();
    labels.push(other ? `Others: ${other}` : 'Others');
  }
  return labels.length > 0 ? labels.join(', ') : '—';
}
